<template>
  <div :class="containerClass">
    <div :class="spinnerClass"></div>
    <span v-if="text" :class="textClass">{{ text }}</span>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'

  interface Props {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    color?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'white'
    text?: string
    inline?: boolean
    centered?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'md',
    color: 'primary',
    text: '',
    inline: false,
    centered: false,
  })

  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  }

  const colorClasses = {
    primary: 'border-primary',
    secondary: 'border-secondary',
    success: 'border-success',
    danger: 'border-error',
    warning: 'border-warning',
    info: 'border-info',
    white: 'border-white',
  }

  const textSizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
  }

  const containerClass = computed(() => {
    const classes = []

    if (props.inline) {
      classes.push('inline-flex items-center space-x-2')
    } else {
      classes.push('flex items-center space-x-2')
    }

    if (props.centered && !props.inline) {
      classes.push('justify-center')
    }

    return classes.join(' ')
  })

  const spinnerClass = computed(() => {
    return [
      'animate-spin rounded-full border-2 border-t-transparent',
      sizeClasses[props.size],
      colorClasses[props.color],
    ].join(' ')
  })

  const textClass = computed(() => {
    return [
      'font-medium',
      textSizeClasses[props.size],
      props.color === 'white' ? 'text-primary-content' : 'text-base-content',
    ].join(' ')
  })
</script>
